import { useCallback, useContext, useEffect, useReducer } from 'react';
import { Just, Nothing } from 'purify-ts/Maybe';
import { isSafari } from 'react-device-detect';
import { BrowserSearchContext } from '../provider';
import { areRequestsEqual } from '../queryRequest';
import { buildStateMachine } from '../stateMachine';
const initialState = {
    status: 'idle',
    isFetching: false,
};
const fromIdleToLoading = (state, action) => (state.status === 'idle' && action.type === 'searchStarted' ?
    Just({
        status: 'loading',
        request: action.request,
        abort: action.abort,
        isFetching: true,
        trigger: action.trigger
    }) : Nothing);
const fromLoadingToLoading = (state, action) => {
    if (state.status === 'loading' && action.type === 'searchStarted') {
        if (getShouldAbortRequest(state.request, action)) {
            state.abort();
        }
        return Just({
            status: 'loading',
            request: action.request,
            abort: action.abort,
            isFetching: true,
            trigger: action.trigger,
        });
    }
    return Nothing;
};
const fromLoadingToError = (state, action) => (state.status === 'loading' && action.type === 'searchFailed' && state.request === action.request ?
    Just({
        status: 'error',
        request: action.request,
        error: action.error,
        isFetching: false,
    }) : Nothing);
const fromLoadingToSuccess = (state, action) => (state.status === 'loading' && action.type === 'searchCompleted' && state.request === action.request ?
    Just({
        status: 'success',
        request: action.request,
        response: action.response,
        isFetching: false,
    }) : Nothing);
const fromSuccessToStale = (state, action) => (state.status === 'success' && action.type === 'searchStarted' ?
    Just({
        status: 'stale',
        request: state.request,
        response: state.response,
        newRequest: action.request,
        abort: action.abort,
        isFetching: true,
        trigger: action.trigger,
        areStatsStale: getAreStatsStale(state.request, action),
    }) : Nothing);
const fromStaleToSuccess = (state, action) => (state.status === 'stale' && action.type === 'searchCompleted' && state.newRequest === action.request ?
    Just({
        status: 'success',
        request: action.request,
        response: action.response,
        isFetching: false,
    }) : Nothing);
const fromStaleToStale = (state, action) => {
    if (state.status === 'stale' && action.type === 'searchStarted') {
        if (getShouldAbortRequest(state.newRequest, action)) {
            state.abort();
        }
        return Just({
            status: 'stale',
            request: state.request,
            response: state.response,
            newRequest: action.request,
            abort: action.abort,
            isFetching: true,
            trigger: action.trigger,
            areStatsStale: getAreStatsStale(state.request, action),
        });
    }
    return Nothing;
};
const fromStaleToError = (state, action) => (state.status === 'stale' && action.type === 'searchFailed' && state.newRequest === action.request ?
    Just({
        status: 'error',
        request: action.request,
        error: action.error,
        isFetching: false,
    }) : Nothing);
const fromErrorToLoading = (state, action) => (state.status === 'error' && action.type === 'searchStarted' ?
    Just({
        status: 'loading',
        request: action.request,
        abort: action.abort,
        isFetching: true,
        trigger: action.trigger
    }) : Nothing);
const getAreStatsStale = (previousRequest, searchStartedAction) => (previousRequest.filtersApplied !== searchStartedAction.request.filtersApplied ||
    previousRequest.filterConfig !== searchStartedAction.request.filterConfig ||
    searchStartedAction.trigger === 'store-mutation');
const getShouldAbortRequest = (previousRequest, searchStartedAction) => (!isSafari && (!areRequestsEqual(previousRequest, searchStartedAction.request) ||
    searchStartedAction.trigger === 'store-mutation'));
export const buildReducer = () => {
    const stateTransitions = [fromIdleToLoading, fromLoadingToLoading, fromLoadingToError, fromLoadingToSuccess, fromSuccessToStale, fromStaleToStale, fromStaleToSuccess, fromStaleToError, fromErrorToLoading];
    return buildStateMachine(stateTransitions);
};
export const useQuery = (request) => {
    const queryClient = useContext(BrowserSearchContext);
    const [state, dispatch] = useReducer(buildReducer(), initialState);
    const runQuery = useCallback(({ trigger }) => {
        const requestInstance = Object.assign({}, request);
        const [seachResponsePromise, abortSearch] = queryClient.queryStore(requestInstance);
        dispatch({ type: 'searchStarted', request: requestInstance, abort: abortSearch, trigger });
        seachResponsePromise
            .then(searchResponse => {
            dispatch({ type: 'searchCompleted', response: searchResponse, request: requestInstance });
        })
            .catch(error => {
            console.log(error, request);
            dispatch({ type: 'searchFailed', request: requestInstance, error });
        });
    }, [request, queryClient]);
    useEffect(() => {
        queryClient.subscribeToStoreChange(request.storeId)(runQuery);
        return () => {
            queryClient.unsubscribeToStoreChange(request.storeId)(runQuery);
        };
    }, [request.storeId, queryClient, runQuery]);
    useEffect(() => {
        runQuery({ trigger: 'request-change' });
    }, [runQuery]);
    return state;
};
